import * as React from 'react'
import { ReactNode, useCallback } from 'react'
import { Popover, PopoverTrigger } from '../ui/popover'
import { Component, SugaredQualifiedEntityList, useEntity, useEntityBeforePersist } from '@contember/interface'
import { Button } from '../ui/button'
import { SugaredRelativeSingleEntity } from '@contember/interface'
import { ChevronDownIcon, XIcon } from 'lucide-react'
import {
	SelectCreateNewTrigger,
	SelectDefaultPlaceholderUI,
	SelectInputActionsUI,
	SelectInputUI,
	SelectInputWrapperUI,
	SelectListItemUI,
	SelectPopoverContent,
} from './ui'
import { SelectListInner } from './list'
import { Select, SelectDataView, SelectEachValue, SelectItemTrigger, SelectOption, SelectPlaceholder } from '@contember/react-select'
import { CreateEntityDialog } from './create-new'
import { SelectDefaultFilter } from './filter'
import {
	createFilterHandler,
	DataViewFilterHandlerRegistry,
	DataViewQueryFilterName,
	DataViewSortingDirections,
	DataViewUnionFilterFields,
	createUnionTextFilter,
} from '@contember/react-dataview'
import { useFormFieldId } from '@contember/react-form'
import { dict } from '../dict'

export type SelectInputProps = {
	field: SugaredRelativeSingleEntity['field']
	children: ReactNode
	options?: SugaredQualifiedEntityList['entities']
	placeholder?: ReactNode
	createNewForm?: ReactNode
	queryField?: DataViewUnionFilterFields
	initialSorting?: DataViewSortingDirections
	required?: boolean
}

export const SelectInput = Component<SelectInputProps>(
	({ field, queryField, options, children, placeholder, createNewForm, initialSorting, required }) => {
		const [open, setOpen] = React.useState(false)
		const id = useFormFieldId()
		const getEntity = useEntity().getAccessor
		useEntityBeforePersist(
			useCallback(() => {
				if (!required) {
					return
				}
				const entity = getEntity().getEntity({ field })
				if (!entity.existsOnServer && !entity.hasUnpersistedChanges) {
					entity.addError(dict.errors.required)
				}
			}, [field, getEntity, required]),
		)

		// Handling UUIDs v queryField
		const filterTypes = React.useMemo((): DataViewFilterHandlerRegistry | undefined => {
			if (!queryField) {
				return undefined
			}
			const fields = Array.isArray(queryField) ? queryField : [queryField]
			const hasId = fields.includes('id')
			if (!hasId) {
				return undefined
			}
			const textFields = fields.filter(f => f !== 'id')
			const textHandler = textFields.length > 0 ? createUnionTextFilter(textFields) : undefined
			const idEqHandler = createFilterHandler<{ query?: string }>({
				createFilter: ({ query }) => {
					if (!query) return undefined
					// UUID v4/varianta
					const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(query)
					if (!isUuid) return undefined
					return { id: { eq: query } }
				},
				isEmpty: f => !f.query,
				identifier: { id: Symbol('idEq'), params: {} },
			})

			return {
				[DataViewQueryFilterName]: (artifact, options) => {
					const parts = []
					if (textHandler) {
						const text = textHandler(artifact, options)
						if (text) parts.push(text)
					}
					const idPart = idEqHandler(artifact, options)
					if (idPart) parts.push(idPart)
					if (parts.length === 0) return undefined
					return { or: parts as any }
				},
			}
		}, [queryField])

		return (
			<Select field={field} onSelect={() => setOpen(false)} options={options}>
				<div className="flex gap-1 items-center">
					<Popover open={open} onOpenChange={setOpen}>
						<SelectInputWrapperUI>
							<PopoverTrigger asChild>
								<SelectInputUI id={id ? `${id}-input` : undefined}>
									<SelectPlaceholder>{placeholder ?? <SelectDefaultPlaceholderUI />}</SelectPlaceholder>
									<SelectEachValue>{children}</SelectEachValue>
								</SelectInputUI>
							</PopoverTrigger>
							<SelectInputActionsUI>
								<SelectEachValue>
									<SelectItemTrigger>
										<Button size={'xs'} variant={'ghost'}>
											<XIcon className={'w-4 h-4'} />
										</Button>
									</SelectItemTrigger>
								</SelectEachValue>
								<ChevronDownIcon className={'w-4 h-4'} />
							</SelectInputActionsUI>
						</SelectInputWrapperUI>
						<SelectPopoverContent>
							<SelectDataView initialSorting={initialSorting} queryField={queryField} filterTypes={filterTypes}>
								<SelectListInner filterToolbar={<SelectDefaultFilter />}>
									<SelectOption>
										<SelectItemTrigger>
											<SelectListItemUI>{children}</SelectListItemUI>
										</SelectItemTrigger>
									</SelectOption>
								</SelectListInner>
							</SelectDataView>
						</SelectPopoverContent>
					</Popover>
					{createNewForm && <CreateEntityDialog trigger={<SelectCreateNewTrigger />}>{createNewForm}</CreateEntityDialog>}
				</div>
			</Select>
		)
	},
	({ field, children, options }) => {
		return (
			<Select field={field} options={options}>
				{children}
			</Select>
		)
	},
)
